import React from 'react';
import { Container, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import './FeatureComparisonChart.css';

/**
 * FeatureComparisonChart – reusable comparison table for plans.
 * Props:
 *  - comparisonFeaturesList: Array<{feature:string, starter:ReactNode, pro:ReactNode, icon?:ReactNode}>
 *  - featureDefinitions?: Array<{name:string, description?:string, comparisonTooltip?:string}>
 *  - onUpgradeClick: function invoked when Pro CTA clicked
 */
const FeatureComparisonChart = ({
  comparisonFeaturesList,
  featureDefinitions = [],
  title = '',
  leftColumnLabel = 'Plan A',
  rightColumnLabel = 'Plan B',
  renderCTA = null,
}) => {
  const getTooltipText = (featureName) => {
    const def = Array.isArray(featureDefinitions)
      ? featureDefinitions.find((d) => d.name === featureName)
      : null;
    return def?.comparisonTooltip || def?.description || '';
  };

  // Helper function to wrap values in badges if they're simple text
  const formatCellValue = (value, columnType) => {
    if (typeof value === 'string' && !value.includes('<') && !value.includes('✓') && !value.includes('✗')) {
      const badgeClass = columnType === 'starter' 
        ? 'feature-comparison-chart-badge feature-comparison-chart-badge-starter'
        : 'feature-comparison-chart-badge feature-comparison-chart-badge-pro';
      return <span className={badgeClass}>{value}</span>;
    }
    return value;
  };

  return (
    <div className="feature-comparison-chart-panel" data-testid="feature-comparison-chart">
      <Container className="py-3">
        <section className="feature-comparison-chart-content my-5">
          {title && <h2 className="text-center mb-4 section-title">{title}</h2>}
          <Table bordered responsive className="feature-comparison-chart-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>{leftColumnLabel}</th>
                <th>{rightColumnLabel}</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(comparisonFeaturesList) && comparisonFeaturesList.map((item, idx) => {
                const tooltipText = getTooltipText(item.feature);
                const featureCell = (
                  <span className="feature-comparison-chart-feature-with-tooltip">
                    {item.icon && <span className="me-2">{item.icon}</span>}
                    <span className="feature-comparison-chart-feature-text">{item.feature}</span>
                    {tooltipText && (
                      <FaInfoCircle className="feature-comparison-chart-tooltip-indicator" />
                    )}
                  </span>
                );

                const customTooltip = tooltipText ? (
                  <Tooltip className="feature-comparison-chart-custom-tooltip">
                    <div className="feature-comparison-chart-tooltip-content">
                      <strong>{item.feature}</strong>
                      <div className="feature-comparison-chart-tooltip-description">
                        {tooltipText}
                      </div>
                    </div>
                  </Tooltip>
                ) : null;

                return (
                  <tr key={idx}>
                    <td className="feature-comparison-chart-feature-cell">
                      {tooltipText ? (
                        <OverlayTrigger 
                          overlay={customTooltip} 
                          placement="right"
                          delay={{ show: 250, hide: 150 }}
                        >
                          <div style={{ display: 'inline-block' }}>
                            {featureCell}
                          </div>
                        </OverlayTrigger>
                      ) : featureCell}
                    </td>
                    <td>{formatCellValue(item.starter, 'starter')}</td>
                    <td>{formatCellValue(item.pro, 'pro')}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {typeof renderCTA === 'function' && (
            <div className="feature-comparison-chart-cta">
              {renderCTA()}
            </div>
          )}
        </section>
      </Container>
    </div>
  );
};

export default FeatureComparisonChart; 